/**
 * web/src/lib/ws/WebSocketProvider.tsx
 * Single root WebSocket connection (UI-09, Pitfall 11). StrictMode-safe
 * via module-level connection ref guard. Reconnection: exponential
 * backoff 1s→2s→4s→8s capped at 30s (CONTEXT.md D-2.3). On reconnect:
 * full qc.invalidateQueries() — safest catch-up for missed events.
 *
 * Zombie-socket recovery (2026-07-21): a socket killed by laptop sleep or a
 * network change often never fires `close` — it sits OPEN receiving nothing,
 * and with refetchOnWindowFocus off the dashboard froze until a manual
 * refresh. Two guards force such sockets through the reconnect path: a
 * watchdog that treats 75s of silence (2.5 missed server pings) as dead, and
 * visibilitychange/online listeners that re-check immediately on wake.
 *
 * useWS() exposes the connection enum to the Sidebar's status dot
 * (green/amber/red per design handoff §App Shell §Sidebar).
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { handleBroadcast } from './handleBroadcast';
import { redirectToLogin } from '../queryClient';
import type { BroadcastEvent } from './types';

export type WSStatus = 'connected' | 'reconnecting' | 'disconnected';

const WSCtx = createContext<WSStatus>('disconnected');

/** Hook returning the live WS connection status. */
export function useWS(): WSStatus {
  return useContext(WSCtx);
}

// Module-level guard: StrictMode mounts the effect twice in dev; this
// prevents two simultaneous WebSocket connections (Pitfall 11).
let socketRef: WebSocket | null = null;
let reconnectAttempts = 0;
let mountCount = 0;
// Timestamp of the last frame received (broadcast OR the server's 30s
// {"type":"ping"} heartbeat). The watchdog uses server-ping silence to detect
// ZOMBIE sockets: after laptop sleep / a network change the browser-side
// socket often stays readyState OPEN but is dead — no `close` ever fires, and
// with refetchOnWindowFocus off the page would stay frozen until a manual
// refresh. Force-closing routes into the normal reconnect + invalidate path.
let lastMessageAt = 0;
// Distinguishes the initial page-load connect from a REconnect: only the
// latter cancels in-flight fetches before the catch-up invalidation (their
// responses predate the gap; a first-load fetch would otherwise swallow the
// invalidation — see invalidateFresh in handleBroadcast.ts).
let hasConnectedOnce = false;

// 2.5 missed server heartbeats (HEARTBEAT_INTERVAL_S = 30s in api/ws.py).
const STALE_SOCKET_MS = 75_000;
const WATCHDOG_INTERVAL_MS = 15_000;

/**
 * Root WebSocket provider. Mount EXACTLY ONCE under <QueryClientProvider>
 * — the `qc.invalidateQueries` calls in handleBroadcast/onReconnect rely
 * on the same QueryClient instance the rest of the app reads through.
 */
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<WSStatus>('disconnected');
  const seenEventIds = useRef(new Set<string>());

  useEffect(() => {
    mountCount += 1;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (cancelled) return;
      // Reuse existing open socket if StrictMode double-mounts.
      if (socketRef && socketRef.readyState === WebSocket.OPEN) {
        setStatus('connected');
        return;
      }
      if (socketRef && socketRef.readyState !== WebSocket.CLOSED) {
        try {
          socketRef.close();
        } catch {
          // ignore — about to be replaced
        }
      }
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
      socketRef = ws;

      ws.addEventListener('open', () => {
        if (cancelled) return;
        const isReconnect = hasConnectedOnce;
        hasConnectedOnce = true;
        reconnectAttempts = 0;
        lastMessageAt = Date.now();
        setStatus('connected');
        // D-2.3: full invalidation on reconnect. On a REconnect, cancel
        // in-flight fetches first — their responses were read during the
        // gap, and a first-load fetch would swallow the invalidation (RQ
        // dedupes onto the in-flight promise while data is undefined).
        if (isReconnect) {
          void qc.cancelQueries().then(() => qc.invalidateQueries());
        } else {
          qc.invalidateQueries();
        }
      });

      ws.addEventListener('message', (msgEvent) => {
        // Any frame — including the server's {"type":"ping"} heartbeat, which
        // fails the eventId check below — proves the socket is alive.
        lastMessageAt = Date.now();
        try {
          const ev = JSON.parse(msgEvent.data as string) as BroadcastEvent;
          if (!ev.eventId || seenEventIds.current.has(ev.eventId)) return;
          seenEventIds.current.add(ev.eventId);
          // Bound the dedup set so it doesn't grow unbounded.
          if (seenEventIds.current.size > 5000) {
            seenEventIds.current = new Set(Array.from(seenEventIds.current).slice(-2500));
          }
          handleBroadcast(qc, ev);
        } catch {
          // Malformed payload — ignore (server fans out only typed events).
        }
      });

      ws.addEventListener('close', (closeEvent) => {
        if (cancelled) return;
        // 1008 (policy violation) is how the server rejects an unauthenticated
        // /ws — the session expired. Reconnecting would loop forever against a
        // dead session, so stop and route to login instead (F13). Abnormal
        // closes (1006 network blip, 1001 going-away) keep the backoff retry.
        if (closeEvent.code === 1008) {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          setStatus('disconnected');
          redirectToLogin();
          return;
        }
        setStatus('reconnecting');
        const delay = Math.min(1000 * 2 ** reconnectAttempts, 30_000);
        reconnectAttempts += 1;
        reconnectTimer = setTimeout(connect, delay);
      });

      ws.addEventListener('error', () => {
        // close handler runs after error — let it drive the reconnect.
      });
    }

    /**
     * Force the stale/dead socket back through the reconnect path. An OPEN
     * zombie is close()d — the close handler then schedules connect() (with
     * attempts reset below, that's a ~1s retry). A CLOSED socket reconnects
     * immediately, skipping any pending long backoff.
     */
    function forceReconnect() {
      if (cancelled) return;
      reconnectAttempts = 0;
      if (socketRef && socketRef.readyState === WebSocket.OPEN) {
        try {
          socketRef.close();
        } catch {
          // ignore — close handler drives the reconnect either way
        }
      } else if (!socketRef || socketRef.readyState === WebSocket.CLOSED) {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        connect();
      }
      // CONNECTING / CLOSING: an outcome (open or close) is already on its way.
    }

    // Zombie-socket watchdog: the server pings every 30s, so an OPEN socket
    // that's heard nothing for STALE_SOCKET_MS is dead — force-reconnect.
    const watchdog = setInterval(() => {
      if (
        socketRef &&
        socketRef.readyState === WebSocket.OPEN &&
        Date.now() - lastMessageAt > STALE_SOCKET_MS
      ) {
        forceReconnect();
      }
    }, WATCHDOG_INTERVAL_MS);

    // Wake-up recovery: on tab visible / network back, check the socket NOW
    // instead of waiting out the (sleep-throttled) watchdog or a pending
    // backoff — the reconnect's open handler re-invalidates every query.
    function onWake() {
      if (cancelled) return;
      const stale =
        socketRef &&
        socketRef.readyState === WebSocket.OPEN &&
        Date.now() - lastMessageAt > STALE_SOCKET_MS;
      if (stale || !socketRef || socketRef.readyState === WebSocket.CLOSED) {
        forceReconnect();
      }
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') onWake();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onWake);

    connect();

    return () => {
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onWake);
      cancelled = true;
      mountCount -= 1;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      // Only tear down the connection when the LAST mount unmounts.
      // StrictMode mounts → cleanups → re-mounts; the cleanup must not
      // close the socket the re-mount expects to reuse.
      if (mountCount === 0 && socketRef) {
        try {
          socketRef.close();
        } catch {
          // ignore
        }
        socketRef = null;
      }
    };
  }, [qc]);

  return <WSCtx.Provider value={status}>{children}</WSCtx.Provider>;
}
